import React, { useState } from "react";
import { Check, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "./Card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./Select";
import { cn } from "../../lib/utils";

export function Pricing({
  icon,
  title,
  subtitle,
  tiers,
  isAnnual = false,
  footerTitle,
  footerDescription,
  footerEligibility,
  footerSteps,
  footerButtonText,
  footerButtonLink,
  className,
}) {
  const [selectedOptions, setSelectedOptions] = useState({});

  return (
    <div className={cn("w-full py-12 sm:py-16 px-4", className)}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        {title && (
          <div className="text-center mb-12">
            {icon && <div className="flex justify-center mb-4">{icon}</div>}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-balance text-slate-900">
              {title}
            </h2>
            <p className="text-slate-500 text-base sm:text-lg text-balance max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-8">
          {tiers.map((tier, index) => (
            <Card
              key={index}
              className={cn(
                "p-6 sm:p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-apple-xl",
                tier.isPrimary
                  ? "ring-2 ring-violet-500 bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-apple-xl border-violet-500"
                  : "card-glow-top"
              )}
            >
              {tier.isPrimary && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full bg-white text-violet-700 shadow-apple z-10">
                  Most Popular
                </span>
              )}

              {/* Tier Header */}
              <div className="mb-5">
                <h3
                  className={cn(
                    "text-xl sm:text-2xl font-bold mb-2",
                    tier.isPrimary ? "text-white" : "text-slate-900"
                  )}
                >
                  {tier.name}
                </h3>
                <p
                  className={cn(
                    "text-sm leading-relaxed",
                    tier.isPrimary ? "text-white/70" : "text-slate-500"
                  )}
                >
                  {tier.description}
                </p>
              </div>

              {/* Price */}
              <div className="mb-5">
                {tier.monthlyPrice !== undefined ? (
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span
                        className={cn(
                          "text-3xl sm:text-4xl font-black",
                          tier.isPrimary ? "text-white" : "text-slate-900"
                        )}
                      >
                        ₹{isAnnual ? tier.annualPrice : tier.monthlyPrice}
                      </span>
                      <span
                        className={cn(
                          "text-sm",
                          tier.isPrimary ? "text-white/60" : "text-slate-400"
                        )}
                      >
                        /{isAnnual ? "year" : "mo"}
                      </span>
                    </div>
                    <p
                      className={cn(
                        "text-xs mt-1",
                        tier.isPrimary ? "text-white/50" : "text-slate-400"
                      )}
                    >
                      ~${isAnnual ? tier.annualPriceUSD : tier.monthlyPriceUSD}{" "}
                      USD /{isAnnual ? "yr" : "mo"}
                    </p>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "text-xl font-semibold",
                      tier.isPrimary ? "text-white" : "text-slate-900"
                    )}
                  >
                    {tier.priceLabel}
                  </div>
                )}
              </div>

              {/* CTA Button */}
              <Link
                to={tier.buttonLink || "/contact"}
                className={cn(
                  "w-full mb-5 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all btn-shine",
                  tier.isPrimary
                    ? "bg-white text-violet-700 hover:bg-violet-50"
                    : "bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:shadow-apple-lg"
                )}
              >
                {tier.buttonText}
              </Link>

              {/* Options Select */}
              {tier.options && tier.options.length > 0 && (
                <div className="mb-5">
                  <Select
                    value={
                      selectedOptions[tier.name] ||
                      tier.defaultOption ||
                      tier.options[0]
                    }
                    onValueChange={(value) =>
                      setSelectedOptions((prev) => ({
                        ...prev,
                        [tier.name]: value,
                      }))
                    }
                  >
                    <SelectTrigger
                      className={cn(
                        "w-full text-xs sm:text-sm",
                        tier.isPrimary
                          ? "bg-white/10 border-white/20 text-white [&>svg]:text-white/50"
                          : "bg-slate-50 border-slate-200 text-slate-700"
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tier.options.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Features Title */}
              {tier.featuresTitle && (
                <div
                  className={cn(
                    "mb-4 pb-4 border-b text-sm font-medium",
                    tier.isPrimary
                      ? "text-white/80 border-white/15"
                      : "text-slate-700 border-slate-100"
                  )}
                >
                  {tier.featuresTitle}
                </div>
              )}

              {/* Features List */}
              <div className="space-y-3 flex-1">
                {tier.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start gap-3">
                    <Check
                      className={cn(
                        "w-4 h-4 flex-shrink-0 mt-0.5",
                        tier.isPrimary ? "text-violet-200" : "text-violet-600"
                      )}
                    />
                    <span
                      className={cn(
                        "text-sm leading-relaxed flex-1",
                        tier.isPrimary ? "text-white/80" : "text-slate-500"
                      )}
                    >
                      {feature.text}
                    </span>
                    {feature.hasInfo && (
                      <Info
                        className={cn(
                          "w-4 h-4 flex-shrink-0 mt-0.5 cursor-help",
                          tier.isPrimary ? "text-white/30" : "text-slate-300"
                        )}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Footer Banner */}
        {footerTitle && (
          <Card className="p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
              {/* Left — Title & Description */}
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-bold mb-2 text-slate-900">
                  {footerTitle}
                </h3>
                {footerDescription && (
                  <p className="text-slate-500 text-sm mb-4">{footerDescription}</p>
                )}
                {footerButtonText && (
                  <Link
                    to={footerButtonLink || "/contact"}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-apple-lg hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap btn-shine"
                  >
                    {footerButtonText}
                  </Link>
                )}
              </div>

              {/* Middle — Eligibility */}
              {footerEligibility && footerEligibility.length > 0 && (
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Who's eligible</p>
                  <ul className="space-y-2">
                    {footerEligibility.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Right — Steps */}
              {footerSteps && footerSteps.length > 0 && (
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">How to claim</p>
                  <ol className="space-y-2">
                    {footerSteps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
